import React from "react";
import useExcelUpload, {
  generateRandomAmount,
  generateRandomDescription,
} from "../../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../utils/UploadExcelEnhanced";
import { getCaSettingByKey } from "../../../../builders/settings/CaSetting";

const getInstructions = (assetLines, caTypeOptions) => [
  ["Col", "Comments"],
  [
    "A",
    assetLines.map((row) => row.assetCategory).length > 0
      ? "Ensure that asset category name matches asset category created in FA summary. Available options are:" +
        assetLines.map((row) => row.assetCategory).join(", ")
      : "No Category available. You should add atleast one category before you continue to upload your excel file.",
  ],
  ["B", "Description (Max 60 characters)."],
  [
    "C",
    "Ensure that Col C is based on the following options: " +
      caTypeOptions.join(", "),
  ],
  [
    "D",
    'Number of years of claim. Where CA name has a fixed number of years of claim, system value will replace the uploaded value. For non-capital allowance types (i.e. Renovation, No claim, Revenue), please indicate no. of years as "0"',
  ],
  [
    "E",
    "Item cost. Decimal places will be rounded up to the nearest whole number.",
  ],
  [
    "F",
    "False if assets are to be claimed in the current year. True if assets are to be deferred in the current year.",
  ],
];

const AdditionExcelUpload = ({
  assetLines,
  caSettings,
  caTypeOptions,
  onExcelAddRows,
  open,
  handleCloseDialog,
}) => {
  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadTemplate,
    handleFileChange,
    handleResetFile,
    handleUpload,
    getTemplateHeadersString,
  } = useExcelUpload({
    fileName: "fa_addition_upload_template.xlsx",
    instructions: getInstructions(assetLines, caTypeOptions),
    sampleData: Array.from({ length: 3 }, (_, index) => ({
      Category: assetLines.map((row) => row.assetCategory)[0],
      Description: generateRandomDescription(),
      "CA Name": caTypeOptions[0],
      "Number of Years": 3,
      Cost: generateRandomAmount(),
      "Deferred?": "false",
    })),
    validationSchema: [
      {
        type: "text",
        required: true,
        fixedValue: assetLines.map((row) => row.assetCategory),
      },
      { type: "text", required: true },
      { type: "text", required: true, fixedValue: caTypeOptions },
      { type: "number", round: true },
      { type: "number", round: true, required: true },
      { type: "boolean", required: true },
    ],
    onUpload: (excelData, rowCount) => {
      if (rowCount > 0) {
        const updatedAssetLines = assetLines.map((asset) => {
          const matchingRows = excelData.filter(
            (row) =>
              row[0] &&
              row[0].toLowerCase() === asset.assetCategory.toLowerCase(),
          );

          if (matchingRows.length > 0) {
            const newAdditions = matchingRows.map((row) => ({
              description: row[1] || "",
              numberOfYears:
                getCaSettingByKey(caSettings, row[2] || "")?.numberOfYears ||
                row[3],
              cost: Math.round(Number(row[4])) || 0,
              isDeferred:
                typeof row[5] === "string"
                  ? row[5].trim().toLowerCase() === "true"
                  : !!row[5],
              CaName: row[2] || "",
            }));

            const updatedAdditions = [
              ...asset.additionsDetails,
              ...newAdditions,
            ];

            const allDeferredForCategory =
              updatedAdditions.length > 0 &&
              updatedAdditions.every((detail) => detail.isDeferred);

            const sumAdditionsDetails = updatedAdditions.reduce(
              (total, detail) => total + detail.cost,
              0,
            );

            return {
              ...asset,
              additionsDetails: updatedAdditions,
              isAllDeferred: allDeferredForCategory,
              isCostAligned: sumAdditionsDetails === asset.cost.additions,
              totalFloating: sumAdditionsDetails,
            };
          }

          return asset;
        });

        onExcelAddRows(updatedAssetLines);
      }
    },
  });

  const fixedValueGroups = [
    {
      label: "Category",
      values: assetLines.map((row) => row.assetCategory),
    },
    {
      label: "CA Name",
      values: caTypeOptions,
    },
  ];

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadTemplate}
      onFileChange={handleFileChange}
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateHeadersString={getTemplateHeadersString()}
      templateInstructions={getInstructions(assetLines, caTypeOptions)}
      fixedValueGroups={fixedValueGroups}
    />
  );
};

export default AdditionExcelUpload;
